import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, distinctUntilChanged, filter, firstValueFrom, map, skip, take, Observable } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { ApiService, USER_ROLES } from '../../services/api.service';
import { DeleteUserFile, DeleteUserFileSuccess, GetJobTitles, GetSkillsList, GetSkillsListSuccess, GetSkillsValidation, GetSkillsValidationSuccess, GetTalentSkills, GetTalentSkillsTree, GetUserSuccess, SaveUserProfileSuccess, SetAppliesCount, SetCompanyLogo, SetHardSkills, SetHardSkillsSuccess, SetHmAboutYou, SetHmCompany, SetHmFinance, SetHmTelegram, SetLastTreadUpdated, SetMessageCount, SetNotifyCount, SetSoftSkills, SetTalentContacts, SetTalentDetails, SetTalentExpectations, SetTalentSeniority, SetTalentSeniorityFailure, SetTalentSenioritySuccess, SetTalentTelegram, SetUserAvatar, SetUserIncognito, SetUserName, SetUserPreData, SetUserPublic, SetUserRole, SetUserScore, TalentDefaultCv, UploadUserFile, UploadUserFileSuccess } from './user.actions';
import { AdminUserAdapter, HmUserAdapter, TalentUserAdapter } from './user.adapter';
import { UserState, selectUserLoading, selectUserName, selectUserEmail, selectUserIncognito, selectUserRole, selectUserRoles, selectTalentExpectations, selectTalentContacts, selectTalentDetails, selectTalentSoftSkills, selectTalentHardSkills, selectTalentHardSkillsList, selectTalentJobTitle, selectTalentJobTitles, selectUserState, selectUserScore, selectHmAboutYou, selectHmCompany, selectHmFinance, selectUserChatId, selectUserSystemChatId, selectNotifyCount, selectMessageCount, selectOnboardingUserRole, selectUserAvatar, selectHmCompanyLogo, selectHmCompanyId, selectUserPublic, selectUserId, selectUserPublicLinkId, selectFiles, selectAppliesCount, selectTalentJobTitleLevel, selectTelegramToken, selectSkillsValidation, selectDefaultCv, selectSalaryRangeParams, selectIsAdmin, selectIsAccountAdmin, selectUserClaims, selectUserAccountId, selectTalentSelectedJobTitles } from './user.store';
import { Actions, ofType } from '@ngrx/effects';
import { CloudinaryUploadService } from '../../services/cloudinary-upload.service';
import { HelperService } from '../../services/helper.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from 'src/environments/environment';
import { LanguageService } from '../../services/language.service';
import { AppComponent } from 'src/app/app.component';

@Injectable({ providedIn: 'root' })
export class UserProxy {

  incognitoNotify$ = new BehaviorSubject<any>(null);
  isOnboarding$ = new BehaviorSubject<boolean>(null);
  isEmailPolicyBreak$ = new BehaviorSubject<boolean>(false);
  isBlocked$ = new BehaviorSubject<boolean>(false);
  isOffline$ = new BehaviorSubject<boolean>(false);
  isMaintenance$ = new BehaviorSubject<boolean>(false);
  skillsValidationNotify$ = new BehaviorSubject<any>(null);
  fileLoadingState$ = this.cloudinaryUploadService.loadingState$;
  loading$ = this.store.select(selectUserLoading);
  currentRole$ = this.store.select(selectUserRole);
  onboardingRole$ = this.store.select(selectOnboardingUserRole);
  userAvatar$ = this.store.select(selectUserAvatar);
  userId$ = this.store.select(selectUserId);
  userName$ = this.store.select(selectUserName);
  userEmail$ = this.store.select(selectUserEmail);
  userClaims$ = this.store.select(selectUserClaims);
  userIncognito$ = this.store.select(selectUserIncognito);
  userPublic$ = this.store.select(selectUserPublic);
  userPublicLinkId$ = this.store.select(selectUserPublicLinkId);
  userChatId$ = this.store.select(selectUserChatId);
  userSystemChatId$ = this.store.select(selectUserSystemChatId);
  talentJobTitles$ = this.store.select(selectTalentJobTitles);
  talentSelectedJobTitles$ = this.store.select(selectTalentSelectedJobTitles);
  talentJobTitle$ = this.store.select(selectTalentJobTitle);
  talentJobTitleLevel$ = this.store.select(selectTalentJobTitleLevel);
  talentHardSkillsList$ = this.store.select(selectTalentHardSkillsList);
  talentHardSkills$ = this.store.select(selectTalentHardSkills);
  talentSoftSkills$ = this.store.select(selectTalentSoftSkills);
  talentDetails$ = this.store.select(selectTalentDetails);
  talentExpectations$ = this.store.select(selectTalentExpectations);
  talentContacts$ = this.store.select(selectTalentContacts);
  hmAboutYou$ = this.store.select(selectHmAboutYou);
  hmCompany$ = this.store.select(selectHmCompany);
  hmCompanyId$ = this.store.select(selectHmCompanyId);
  hmCompanyLogo$ = this.store.select(selectHmCompanyLogo);
  hmFinance$ = this.store.select(selectHmFinance);
  messageCount$ = this.store.select(selectMessageCount).pipe(filter(val => val !== null));
  notifyCount$ = this.store.select(selectNotifyCount);
  isAdmin$ = this.store.select(selectIsAdmin);
  isAccountAdmin$ = this.store.select(selectIsAccountAdmin);
  appliesCount$ = this.store.select(selectAppliesCount);
  userTotalScore$ = this.store.select(selectUserScore);
  userFiles$ = this.store.select(selectFiles);
  talentDefaultCv$ = this.store.select(selectDefaultCv);
  salaryRangeParams$ = this.store.select(selectSalaryRangeParams).pipe(
    distinctUntilChanged((pre: any, curr: any) => JSON.stringify(pre) === JSON.stringify(curr)),
    filter(val => val.country && val.position)
  );
  userSkillsValidation$ = toSignal(this.store.select(selectSkillsValidation));
  telegramToken$ = toSignal(this.store.select(selectTelegramToken));
  userRoles$ = this.store.select(selectUserRoles);
  UserAccountId$ = this.store.select(selectUserAccountId);
  userLatestParsedCv$ = toSignal(this.store.select(selectFiles).pipe(
    map(files => {
      if (files && files.length) {
        const sorted = [...files].sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
        const found = sorted.find(item => item.details && item.details.type && item.details.type === 'cv');
        return found ? found : null
      } else {
        return null;
      }  
    }
  )));
  userScore$ = this.store.select(selectUserState).pipe(
    map(state => {
      if (state.roles.includes(USER_ROLES.talent)) {
        return TalentUserAdapter.calculateTalentScore(state);
      } else if (state.roles.includes(USER_ROLES.hm)) {
        return HmUserAdapter.calculateHmScore(state);
      } else {
        return null;
      }
    })
  );

  constructor(
    private store: Store<UserState>,
    private apiService: ApiService,
    private cloudinaryUploadService: CloudinaryUploadService,
    private actions$: Actions,
    private helperService: HelperService,
    private oauthService: OAuthService,
    private injector: Injector
  ) {
  }

  checkIncognitoWarnPassed() {
    return new Promise((resolve) => {
      if (this.helperService.isBrowser()) {
        this.userIncognito$.pipe(take(1)).subscribe(val => {
          if (val && !localStorage.getItem('noIncognitoNotify')) {
            this.incognitoNotify$.next({ result: false });
            this.incognitoNotify$.pipe(skip(1), take(1)).subscribe(res => {
              resolve(res.result);
              this.incognitoNotify$.next(null);
            });
          } else {
            resolve(true);
          }
        });
      } else {
        resolve(true);
      }
    });
  }

  getUser() {
    return new Promise((resolve) => {
      
      this.helperService.loadWatcherInit();
      // this.helperService.checkVersion();
      this.apiService.checkUser().subscribe((res: any) => {
        
        // if (res && res.error) {
        //   this.isBlocked$.next(true);
          
        //   resolve('blocked'); 
        //   return;
        // }

        this.apiService.getUser().subscribe(payload => {
          
        if (payload && payload.error) {
          this.isBlocked$.next(true);
          resolve({
            redirectTo: 'disabled',
          }); 
          return;
        }

          payload = payload.data;
           const emailPolicyBreak = payload.claims.find(c => c.type === 'EmailPolicyBreak')?.value === 'True';
          if (emailPolicyBreak) {
            this.isEmailPolicyBreak$.next(true);
            resolve({
              redirectTo: 'emailPolicyBreak',
              user: { ...payload }
            });
            return;
          }
          if (payload && payload.email) {
            this.store.dispatch(SetUserPreData({
              id: payload.id,
              email: payload.email,
              name: payload.name,
              onBoardingRole: payload.onBoardingRole,
              files: payload.profile?.files ?? []
            }));
          }

          if (payload && payload.roles && payload.roles.length && payload.roles.includes(USER_ROLES.talent)) {
            this.store.dispatch(GetUserSuccess({ payload: TalentUserAdapter.toStore(payload) })); 
            this.helperService.loadWatcherUserReady(true, !!!payload.roles.length);
          }

          if (payload && payload.roles && payload.roles.length && payload.roles.includes(USER_ROLES.hm)) {
            this.store.dispatch(GetUserSuccess({ payload: HmUserAdapter.toStore(payload) }));
            this.helperService.loadWatcherUserReady(false, !!!payload.roles.length);
          }

          if (payload && payload.roles && payload.roles.length && payload.roles.includes(USER_ROLES.admin)) {
            this.store.dispatch(GetUserSuccess({ payload: AdminUserAdapter.toStore(payload) }));
            this.helperService.loadWatcherUserReady(false, !!!payload.roles.length);
          }

          // Load language from claims after user is loaded
          this.loadLanguageFromUserClaims();

          

          const isOnboarding = payload && payload.roles && !payload.roles.length;
          this.isOnboarding$.next(isOnboarding);
          
          setTimeout(() => {
            if (this.helperService.isBrowser() && !isOnboarding) {
              const token = localStorage.getItem('connect-token');
              if (token) {
                this.apiService.telegramConnect(token).subscribe(() => {
                  this.getUser();
                  setTimeout(() => this.initSkillValidationCheck(), 7000);
                });
                localStorage.removeItem('connect-token');
              }
            }
          }, 1000);

          resolve({
            redirectTo: isOnboarding ? 'onboarding' : 'dashboard',
            user: {...payload}
          });
        }, err => {
          if (err && (err.status === 0 || err.status === 404)) {
            console.error('Server is offline on GET user: ', err);
            this.isOffline$.next(true);
            resolve('blocked');
          }
        });
      }, err => {
        if (err) {
          
          if (err.status === 503 || (err.status === 500 && err.error?.Code === 'MaintainMode')) {
            console.log('Server error on POST user: ', err);
            this.isMaintenance$.next(true);
            resolve('maintenance');
          } else if (err.status === 0 || err.status === 404){
            
            if (!environment.baseDomain.includes('localhost')) {
              console.error('Server is offline: ', err);
              this.isOffline$.next(true); 
              resolve('blocked');
            } else 
            {            this.isMaintenance$.next(true);
              resolve('maintenance');}

          } else if (err.error?.Code === 'UserAccountIsNotActive') {
            console.log('Server error on POST user: ', err);
            this.isBlocked$.next(true);
            resolve('blocked');
          }
        }
        return;
      });
    });
  }

  
  setRole(role) {
    this.store.dispatch(SetUserRole({ role }));
  }

  async updateUserName(name) {
    setTimeout(() => this.store.dispatch(SetUserName({ name })));
    return await firstValueFrom(this.actions$.pipe(ofType(SaveUserProfileSuccess)));
  }

  updateUserIncognito(incognito) {
    this.store.dispatch(SetUserIncognito({ incognito }));
  }

  async updateUserPublic(isPublic) {
    setTimeout(() => this.store.dispatch(SetUserPublic({ public: isPublic })));
    return await firstValueFrom(this.actions$.pipe(ofType(SaveUserProfileSuccess)));
  }

  setSoftSkills(skillsList: any[]) {
    this.store.dispatch(SetSoftSkills({ skillsList }));
  }

  async setTalentDetails(details) {
    setTimeout(() => this.store.dispatch(SetTalentDetails({ details })));
    return await firstValueFrom(this.actions$.pipe(ofType(SaveUserProfileSuccess)));
  }

  async setTalentExpectations(expectations) {
    setTimeout(() => this.store.dispatch(SetTalentExpectations({ expectations })));
    return await firstValueFrom(this.actions$.pipe(ofType(SaveUserProfileSuccess)));
  }

  async setTalentContacts(contacts) {
    setTimeout(() => this.store.dispatch(SetTalentContacts({ contacts })));
    return await firstValueFrom(this.actions$.pipe(ofType(SaveUserProfileSuccess)));
  }

  async setTalentTelegram(telegram) {
    setTimeout(() => this.store.dispatch(SetTalentTelegram({ telegram })));
    return await firstValueFrom(this.actions$.pipe(ofType(SaveUserProfileSuccess)));
  }

  getTalentSkills() {
    this.store.dispatch(GetTalentSkills());
  }

  getSkillsList() {
    this.store.dispatch(GetSkillsList());
  }

  async setHardSkills(skillsList: any[] = [], noDebounce = false) {
    setTimeout(() => this.store.dispatch(SetHardSkills({ skillsList, noDebounce })));
    return await firstValueFrom(this.actions$.pipe(ofType(SetHardSkillsSuccess)));
  }

  updateTalentAllSkillsList(skillsList: any[] = []) {
    this.store.dispatch(GetSkillsListSuccess({ skillsList }));
  }

  getJobTitles() {
    this.store.dispatch(GetJobTitles());
  }

  getTalentSkillsTree() {
    this.store.dispatch(GetTalentSkillsTree());
  }

  async setTalentSeniority(level) {
    setTimeout(() => this.store.dispatch((SetTalentSeniority({ level }))));
    return await firstValueFrom(this.actions$.pipe(ofType(SetTalentSenioritySuccess, SetTalentSeniorityFailure)));
  }

  async setHmAboutYou(aboutYou) {
    setTimeout(() => this.store.dispatch(SetHmAboutYou({ aboutYou })));
    return await firstValueFrom(this.actions$.pipe(ofType(SaveUserProfileSuccess)));
  }

  async setHmCompany(company) {
    setTimeout(() => this.store.dispatch(SetHmCompany({ company })));
    return await firstValueFrom(this.actions$.pipe(ofType(SaveUserProfileSuccess)));
  }

  async setHmFinance(finance) {
    setTimeout(() => this.store.dispatch(SetHmFinance({ finance })));
    return await firstValueFrom(this.actions$.pipe(ofType(SaveUserProfileSuccess)));
  }

  async setHmTelegram(telegram) {
    setTimeout(() => this.store.dispatch(SetHmTelegram({ telegram })));
    return await firstValueFrom(this.actions$.pipe(ofType(SaveUserProfileSuccess)));
  }

  setMessageCount(count) {
    this.store.dispatch(SetMessageCount({ count }));
  }

  setNotifyCount(count) {
    this.store.dispatch(SetNotifyCount({ count }));
  }

  setAppliesCount(count) {
    this.store.dispatch(SetAppliesCount({ count }));
  }

  setLastTreadUpdated(threadId) {
    this.store.dispatch(SetLastTreadUpdated({ threadId }));
  }

  setTotalUserScore(score) {
    this.store.dispatch(SetUserScore({ score }));
  }

  setUserAvatar(payload) {
    this.store.dispatch(SetUserAvatar({ payload }));
  }

  setCompanyLogo(payload) {
    this.store.dispatch(SetCompanyLogo({ payload }));
  }

  async uploadUserFile(payload) {
    setTimeout(() => this.store.dispatch(UploadUserFile({ payload })));
    return await firstValueFrom(this.actions$.pipe(ofType(UploadUserFileSuccess)));
  }

  async deleteUserFile(payload) {
    setTimeout(() => this.store.dispatch(DeleteUserFile({ payload })));
    return await firstValueFrom(this.actions$.pipe(ofType(DeleteUserFileSuccess)));
  }

  setTalentDefaultCv(payload) {
    this.store.dispatch(TalentDefaultCv({ payload }))
  }

  requestCompanyChange() {
    return this.apiService.requestCompanyChange();
  }

  reportCompanyChange(text) {
    return this.apiService.reportCompanyChange({ text });
  }

  async getSkillsValidation() {
    setTimeout(() => this.store.dispatch(GetSkillsValidation()));
    return await firstValueFrom(this.actions$.pipe(ofType(GetSkillsValidationSuccess)));
  }

  activateSkillsValidation() {
    return this.apiService.activateSkillsValidation();
  }

  initSkillValidationCheck(noDelay = false) {
    if (environment.skillsValidation) {
      this.currentRole$.pipe(filter(val => val !== null), take(1)).subscribe(async role => {
        if (USER_ROLES.talent === role) {
          await this.getSkillsValidation();
          setTimeout(() => this.checkSkillsCertificate(), noDelay ? 1 : 3000);
        }
      });
    }
  }

   checkSkillsCertificate() {
    let badgeResults = { jobTitleBadge: [], skillBadge: [], branchBadge: [], softSkillBadge: [] };
    let validationResults = this.userSkillsValidation$() ? { ...this.userSkillsValidation$() } : null;
    if (validationResults) {
      if (validationResults.userValidatedJobTitles && validationResults.userValidatedJobTitles.length) {
        validationResults = {
          ...validationResults,
          userValidatedJobTitles: validationResults.userValidatedJobTitles.map(item => {
            if (item.isValidated && !item.isViewed) {
              badgeResults = { 
                ...badgeResults, 
               jobTitleBadge: [
                ...badgeResults.jobTitleBadge,
                { jobTitle: item.jobTitle, reportUrl: item.reportUrl }
              ]
            
              };
              this.apiService.setSkillsValidationViewed(item.jobTitle).subscribe();
              return { ...item, isViewed: true }
            } else {
              return item;
            }
          })
        };
      }
      if (validationResults.userValidatedSoftSkills && validationResults.userValidatedSoftSkills.length) {
        validationResults = {
          ...validationResults,
          userValidatedSoftSkills: validationResults.userValidatedSoftSkills.map(item => {
            if (item.isValidated && !item.isViewed) {
              badgeResults = { 
                ...badgeResults, 
               softSkillBadge: [
                ...badgeResults.softSkillBadge,
                { jobTitle: item.jobTitle, reportUrl: item.reportUrl }
              ]
            };
              this.apiService.setSkillsValidationViewed(null, null, null, item.jobTitle).subscribe();
              return { ...item, isViewed: true }
            } else {
              return item;
            }
          })
        };
      }
      if (validationResults.userValidatedSkills && validationResults.userValidatedSkills.length) {
        validationResults = {
          ...validationResults,
          userValidatedSkills: validationResults.userValidatedSkills.map(item => {
            if (item.isValidated && !item.isViewed) {
              badgeResults = { 
                ...badgeResults, 
                skillBadge: [
                ...badgeResults.skillBadge,
                { skill: item.skill, reportUrl: item.reportUrl }
              ]
              };
              this.apiService.setSkillsValidationViewed(null, item.skill).subscribe();
              return { ...item, isViewed: true }
            } else {
              return item;
            }
          })
        };
      }
    if (validationResults.userValidatedBranches && validationResults.userValidatedBranches.length) {
      validationResults = {
        ...validationResults,
        userValidatedBranches: validationResults.userValidatedBranches.map(item => {
          if (item.isValidated && !item.isViewed) {
            badgeResults = { 
                ...badgeResults, 
                branchBadge: [
                ...badgeResults.branchBadge,
                { branch: item.branch, reportUrl: item.reportUrl }
              ]
              };
            //badgeResults.branchBadge = [...(badgeResults.branchBadge || []), item.branch, item.reportUrl];
            this.apiService.setSkillsValidationViewed(null, null, item.branch).subscribe();
            return { ...item, isViewed: true };
          }
          return item;
        })
      };
    }
    }
    this.skillsValidationNotify$.next(badgeResults);
    this.store.dispatch(GetSkillsValidationSuccess({ payload: validationResults }))
  }

  getSalaryRange(params) {
    return this.apiService.getSalaryRange(params);
  }
  convert(userClaim: any){
    if (userClaim.valueType.includes("boolean")) {
      return userClaim.value === "true";
    }
    else if (userClaim.valueType.includes("double")) {
      return Number(userClaim.value);
    }
    else if (userClaim.valueType.includes("integer")) {
      return parseInt(userClaim.value, 10);
    }
    else {
      return userClaim.value;
    }
  }
getClaims(): Observable<Record<string,any>> {
  const claims: any = this.oauthService.getIdentityClaims();

  this.userClaims$
    .pipe(filter(userClaims => userClaims !== null))
    .subscribe(userClaims => {
      
      userClaims.forEach(userClaim => {
        claims[userClaim.type] = this.convert(userClaim);
      });
    });
    return claims;
}
  /**
   * Check if user has a specific claim
   * @param claimName The name of the claim to check
   * @returns Observable<boolean> indicating if the claim exists
   */
  hasClaim(claimName: string): Observable<boolean> {
    return new Observable<boolean>(observer => {
      try {
        const claims = this.getClaims();
        const hasClaim = claims && claims[claimName] !== undefined;
        observer.next(hasClaim);
        observer.complete();
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  /**
   * Get the value of a specific claim
   * @param claimName The name of the claim to retrieve
   * @returns Observable<any> with the claim value or null if not found
   */
  getClaimValue(claimName: string): Observable<any> {
    
    return new Observable<any>(observer => {
      try {
        const claims = this.getClaims();
        const claimValue = claims && claims[claimName] !== undefined ? claims[claimName] : null;
        observer.next(claimValue);
        observer.complete();
      } catch (error) {
        observer.next(null);
        observer.complete();
      }
    });
  }

  /**
   * Check if user has any of the specified claims
   * @param claimNames Array of claim names to check
   * @returns Observable<boolean> indicating if any of the claims exist
   */
  hasAnyClaim(claimNames: string[]): Observable<boolean> {
    return new Observable<boolean>(observer => {
      try {
        const claims = this.oauthService.getIdentityClaims();
        const hasAny = claimNames.some(claimName => claims && claims[claimName] !== undefined);
        observer.next(hasAny);
        observer.complete();
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  /**
   * Check if user has all of the specified claims
   * @param claimNames Array of claim names to check
   * @returns Observable<boolean> indicating if all claims exist
   */
  hasAllClaims(claimNames: string[]): Observable<boolean> {
    return new Observable<boolean>(observer => {
      try {
        const claims = this.oauthService.getIdentityClaims();
        const hasAll = claimNames.every(claimName => claims && claims[claimName] !== undefined);
        observer.next(hasAll);
        observer.complete();
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  /**
   * Get all identity claims
   * @returns Observable<any> with all claims object
   */
  getAllClaims(): Observable<any> {
    return new Observable<any>(observer => {
      try {
        const claims = this.oauthService.getIdentityClaims();
        observer.next(claims || {});
        observer.complete();
      } catch (error) {
        observer.next({});
        observer.complete();
      }
    });
  }

  /**
   * Check if user has a specific claim with a specific value
   * @param claimName The name of the claim to check
   * @param expectedValue The expected value of the claim
   * @returns Observable<boolean> indicating if the claim has the expected value
   */
  hasClaimWithValue(claimName: string, expectedValue: any): Observable<boolean> {
    return new Observable<boolean>(observer => {
      try {
        const claims = this.oauthService.getIdentityClaims();
        // add claims from /user
        const claimValue = claims && claims[claimName];
        const hasClaimWithValue = claimValue === expectedValue;
        observer.next(hasClaimWithValue);
        observer.complete();
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  /**
   * Load language preference from user claims
   * Uses lazy injection to avoid circular dependency
   */
  private loadLanguageFromUserClaims(): void {
    try {
      // Lazy inject LanguageService to avoid circular dependency
      const languageService = this.injector.get(LanguageService);
      
      // Get claims synchronously
      const claims: any = this.oauthService.getIdentityClaims();
      
      // Merge with user claims from store
      this.userClaims$
        .pipe(filter(userClaims => userClaims !== null), take(1))
        .subscribe(userClaims => {
          userClaims.forEach(userClaim => {
            claims[userClaim.type] = this.convert(userClaim);
          });
          
          // Load language from claims
          if (claims) {
            languageService.loadLanguageFromClaims(claims);
          }
        });
    } catch (error) {
      console.error('Failed to load language from claims', error);
    }
  }
}
